import {
  AccessChangeAction,
  AccessColumn,
  AccessControlValue,
  AccessLevel,
  AccessSummary,
  LearningDynamicsLesson,
  LessonWorkType,
} from './types'

export const supportsContentOnly = (workType: LessonWorkType) =>
  workType === 'homework'

export const getAccessActionKey = (
  entityType: AccessChangeAction['entityType'],
  entityId: number,
  column: AccessColumn,
) => `${entityType}-${entityId}-${column}`

/**
 * Колонки вложены друг в друга, а не независимы:
 * «Материалы» открыты и при content_only, и при full,
 * «Задания» — только при full. Сочетание «материалы закрыты,
 * задания открыты» невозможно по построению.
 */
const getOpenedCount = (summary: AccessSummary, column: AccessColumn) =>
  column === 'materials'
    ? summary.content_only + summary.full
    : summary.full

export const getAggregateAccessControl = (
  summary: AccessSummary,
  column: AccessColumn,
): AccessControlValue => {
  if (!summary || summary.total === 0) {
    return { state: 'off', disabled: true }
  }

  const opened = getOpenedCount(summary, column)

  if (opened === summary.total) {
    return { state: 'on', disabled: false }
  }

  return { state: opened === 0 ? 'off' : 'mixed', disabled: false }
}

export const getLessonAccessControl = (
  lesson: LearningDynamicsLesson,
  column: AccessColumn,
): AccessControlValue => ({
  state:
    column === 'materials'
      ? lesson.access_level !== 'closed'
        ? 'on'
        : 'off'
      : lesson.access_level === 'full'
        ? 'on'
        : 'off',
  disabled: false,
})

/**
 * У блока и темы конкретного work_type нет: контрольные и пробники
 * внутри них бэкенд отсеивает сам, поэтому считаем режим доступным.
 */
export const getTargetAccessLevel = ({
  column,
  state,
  workType,
}: AccessChangeAction): AccessLevel => {
  const contentOnlyAvailable = !workType || supportsContentOnly(workType)

  if (column === 'materials') {
    return state === 'on'
      ? 'closed'
      : contentOnlyAvailable
        ? 'content_only'
        : 'full'
  }

  return state === 'on'
    ? contentOnlyAvailable
      ? 'content_only'
      : 'closed'
    : 'full'
}

export const getAccessConfirmationText = (action: AccessChangeAction) => {
  const targetLevel = getTargetAccessLevel(action)
  const quotedName = action.entityName ? ` «${action.entityName}»` : ''
  const scope =
    action.entityType === 'block'
      ? `ко всем урокам блока${quotedName}`
      : action.entityType === 'theme'
        ? `ко всем урокам темы${quotedName}`
        : `к уроку${quotedName}`

  if (targetLevel === 'closed') {
    return `Закрыть доступ ${scope}?`
  }

  // понижение с полного доступа: задания закрываем, материалы оставляем
  if (targetLevel === 'content_only' && action.column === 'tasks') {
    return `Закрыть задания ${scope}? Материалы останутся открыты.`
  }

  const accessDescription =
    targetLevel === 'content_only'
      ? 'без возможности выполнять задания'
      : 'с возможностью выполнять задания'

  if (action.entityType === 'block') {
    return `Открыть все уроки блока${quotedName} ${accessDescription}?`
  }

  if (action.entityType === 'theme') {
    return `Открыть все уроки темы${quotedName} ${accessDescription}?`
  }

  return `Открыть доступ к уроку${quotedName} ${accessDescription}?`
}
